import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Download, Plus } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Banner, Empty, Field, NativeSelect, Segmented, errMsg } from '@/components/hr/shared'
import { useCurrency } from '@/contexts/CurrencyContext'
import { addDaysISO, downloadCSV, todayISO } from '@/lib/hr'
import { STATUS_LABEL, dayLabel, slotLabel, statusTone } from '@/lib/trade'
import { toastHelpers } from '@/lib/toast-helpers'
import { cn } from '@/lib/utils'
import type { TradeDelivery, TradeDeliveryFilter, TradeDeliveryStatus } from '@/types/trade'
import { DeliveryForm } from './DeliveryForm'
import { tradeRole, useTradeAccounts } from './TradePage'

// Every delivery, newest day first, grouped by day the way the WhatsApp
// groups read. Click one to see its lines and history, edit it, confirm it,
// mark it delivered or cancel it.

type StatusFilter = 'open' | 'draft' | 'cancelled' | 'all'

export function StatusChip({ status }: { status: TradeDeliveryStatus }) {
  return <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium', statusTone(status))}>{STATUS_LABEL[status]}</span>
}

export function DeliveriesTab({ deliveryId, onDelivery, onSetUp }: { deliveryId?: string; onDelivery: (id: string | undefined) => void; onSetUp: () => void }) {
  const { formatCurrency } = useCurrency()
  const { canEnter, canSetup } = tradeRole()
  const { accounts, isLoading: accountsLoading } = useTradeAccounts()
  const [accountId, setAccountId] = useState('')
  const [status, setStatus] = useState<StatusFilter>('open')
  const [from, setFrom] = useState(addDaysISO(todayISO(), -30))
  const [to, setTo] = useState('')
  const [adding, setAdding] = useState(false)

  const filter: TradeDeliveryFilter = { status, ...(accountId ? { account_id: accountId } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}) }
  const q = useQuery({ queryKey: ['trade', 'deliveries', filter], queryFn: () => apiClient.tradeListDeliveries(filter) })
  const rows = (q.data?.data as TradeDelivery[] | undefined) ?? []

  const days = useMemo(() => {
    const m = new Map<string, TradeDelivery[]>()
    for (const d of rows) m.set(d.delivery_date, [...(m.get(d.delivery_date) ?? []), d])
    return [...m.entries()]
  }, [rows])
  const billable = rows.filter((d) => d.status === 'confirmed' || d.status === 'delivered')

  const exportCSV = () => {
    const header = ['Delivery', 'Date', 'When', 'Client', 'Branch', 'Status', 'Items', 'Goods', 'Fare', 'Total']
    downloadCSV(`trade-deliveries-${todayISO()}.csv`, [
      header,
      ...rows.map((d) => [d.delivery_no, d.delivery_date, slotLabel(d.slot), d.account_name, d.branch_label ?? '', STATUS_LABEL[d.status], d.item_count, d.subtotal, d.fare, d.total]),
    ])
  }

  if (!accountsLoading && accounts.length === 0) {
    return (
      <Empty>
        <p>No clients yet. Add the cafés you supply, with their branches and trade prices, then record deliveries here.</p>
        {canSetup && (
          <Button className="mt-4" size="sm" onClick={onSetUp}>
            Add your first client
          </Button>
        )}
      </Empty>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { id: 'open', label: 'Active' },
              { id: 'draft', label: 'Drafts' },
              { id: 'cancelled', label: 'Cancelled' },
              { id: 'all', label: 'All' },
            ]}
          />
          <NativeSelect value={accountId} onChange={(e) => setAccountId(e.target.value)} className="h-9 w-48" aria-label="Client">
            <option value="">All clients</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </NativeSelect>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" aria-label="From" />
          <span className="pb-2 text-sm text-muted-foreground">to</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" aria-label="To" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!rows.length}>
            <Download className="mr-1.5 h-4 w-4" />
            Export
          </Button>
          {canEnter && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              New delivery
            </Button>
          )}
        </div>
      </div>

      {billable.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {billable.length} deliver{billable.length === 1 ? 'y' : 'ies'} to bill in this view ·{' '}
          <span className="font-medium text-foreground tabular-nums">{formatCurrency(billable.reduce((s, d) => s + d.total, 0))}</span>
        </p>
      )}

      {q.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty>No deliveries in this range.</Empty>
      ) : (
        <div className="space-y-4">
          {days.map(([day, list]) => (
            <div key={day} className="overflow-x-auto rounded-lg border">
              <div className="flex items-center justify-between bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{dayLabel(day)}</span>
                <span className="tabular-nums">{formatCurrency(list.filter((d) => d.status !== 'cancelled' && d.status !== 'draft').reduce((s, d) => s + d.total, 0))}</span>
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y">
                  {list.map((d) => (
                    <tr key={d.id} className="cursor-pointer hover:bg-muted/30" onClick={() => onDelivery(d.id)}>
                      <td className="w-24 px-4 py-2.5 text-muted-foreground">{slotLabel(d.slot) || '—'}</td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{d.account_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {[d.branch_label, d.delivery_no].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {d.item_count} item{d.item_count === 1 ? '' : 's'}
                        {d.missing_prices > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-warning-fg">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {d.missing_prices} unpriced
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground tabular-nums">{d.fare > 0 ? `+ ${formatCurrency(d.fare)} fare` : 'No fare'}</td>
                      <td className="px-4 py-2.5 text-right font-medium tabular-nums">{formatCurrency(d.total)}</td>
                      <td className="w-28 px-4 py-2.5 text-right">
                        <StatusChip status={d.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <DeliveryForm open={adding} onClose={() => setAdding(false)} />
      <DeliverySheet id={deliveryId} onClose={() => onDelivery(undefined)} />
    </div>
  )
}

const EVENT_LABEL: Record<string, string> = {
  created: 'Entered',
  updated: 'Changed',
  confirmed: 'Confirmed',
  delivered: 'Marked delivered',
  cancelled: 'Cancelled',
}

type EventLines = { product: string; qty: number }[]

/** "Brownie 30 → 28, + Lemon cake 2" from an `updated` event's before/after. */
function describeChange(detail: Record<string, unknown>, money: (n: number) => string): string {
  const before = detail.before as { lines?: EventLines; fare?: number; date?: string; slot?: string } | undefined
  const after = detail.after as { lines?: EventLines; fare?: number; date?: string; slot?: string } | undefined
  if (!before || !after) return ''
  const parts: string[] = []
  const was = new Map((before.lines ?? []).map((l) => [l.product, l.qty]))
  const now = new Map((after.lines ?? []).map((l) => [l.product, l.qty]))
  for (const [p, n] of now) {
    const o = was.get(p)
    if (o == null) parts.push(`+ ${p} ${n}`)
    else if (o !== n) parts.push(`${p} ${o} → ${n}`)
  }
  for (const [p, o] of was) if (!now.has(p)) parts.push(`− ${p} ${o}`)
  if (before.fare !== after.fare) parts.push(`fare ${money(before.fare ?? 0)} → ${money(after.fare ?? 0)}`)
  if (before.date !== after.date) parts.push(`date ${before.date} → ${after.date}`)
  if (before.slot !== after.slot) parts.push(`${slotLabel(before.slot ?? '') || '—'} → ${slotLabel(after.slot ?? '') || '—'}`)
  return parts.join(', ') || 'Prices updated'
}

function DeliverySheet({ id, onClose }: { id?: string; onClose: () => void }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { canEnter } = tradeRole()
  const [editing, setEditing] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  const q = useQuery({ queryKey: ['trade', 'delivery', id], queryFn: () => apiClient.tradeGetDelivery(id!), enabled: !!id })
  const d = q.data?.data as TradeDelivery | undefined

  const act = useMutation({
    mutationFn: (a: 'confirm' | 'deliver' | 'cancel') => apiClient.tradeDeliveryAction(id!, a, a === 'cancel' ? reason.trim() : undefined),
    onSuccess: (res) => {
      toastHelpers.success(res.message || 'Saved')
      setCancelling(false)
      setReason('')
      qc.invalidateQueries({ queryKey: ['trade'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t update the delivery', errMsg(err)),
  })

  return (
    <>
      <Sheet open={!!id && !editing} onOpenChange={(o) => !o && onClose()}>
        <SheetContent size="lg">
          <SheetHeader>
            <SheetTitle>{d ? `${d.account_name}${d.branch_label ? ` · ${d.branch_label}` : ''}` : '…'}</SheetTitle>
            <SheetDescription>{d ? [dayLabel(d.delivery_date), slotLabel(d.slot), d.delivery_no].filter(Boolean).join(' · ') : ''}</SheetDescription>
          </SheetHeader>
          <SheetBody className="space-y-5">
            {!d ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <StatusChip status={d.status} />
                  {d.created_by_name && <span className="text-xs text-muted-foreground">Entered by {d.created_by_name}</span>}
                </div>
                {d.missing_prices > 0 && (
                  <Banner tone="warn">Some products have no trade price yet. Set them under Trade prices, then confirm.</Banner>
                )}
                <div className="overflow-hidden rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 font-medium">Product</th>
                        <th className="px-4 py-2 text-right font-medium">Qty</th>
                        <th className="px-4 py-2 text-right font-medium">Price</th>
                        <th className="px-4 py-2 text-right font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(d.lines ?? []).map((l) => (
                        <tr key={l.product_id}>
                          <td className="px-4 py-2">{l.description}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{l.qty}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{l.unit_price == null ? <span className="text-warning-fg">Not set</span> : formatCurrency(l.unit_price)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{l.unit_price == null ? '—' : formatCurrency(l.line_total)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="px-4 py-2 text-muted-foreground" colSpan={3}>
                          Delivery charge
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{d.fare > 0 ? formatCurrency(d.fare) : 'No fare'}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted/40 font-semibold">
                        <td className="px-4 py-2" colSpan={3}>
                          Total
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(d.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {d.note && <p className="text-sm text-muted-foreground">Note: {d.note}</p>}

                <div className="space-y-2">
                  <h3 className="text-sm font-medium">History</h3>
                  <ol className="space-y-1.5 text-sm">
                    {(d.events ?? []).map((e, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="w-32 shrink-0 text-xs text-muted-foreground tabular-nums">
                          {new Date(e.created_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                        </span>
                        <span>
                          <span className="font-medium">{EVENT_LABEL[e.event] ?? e.event}</span>
                          {e.actor_name && <span className="text-muted-foreground"> by {e.actor_name}</span>}
                          {e.event === 'updated' && <span className="text-muted-foreground"> — {describeChange(e.detail, formatCurrency)}</span>}
                          {e.event === 'cancelled' && typeof e.detail.reason === 'string' && <span className="text-muted-foreground"> — {e.detail.reason}</span>}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            )}
          </SheetBody>
          {d && canEnter && d.status !== 'cancelled' && (
            <SheetFooter className="flex-wrap gap-2 sm:justify-between">
              <Button variant="ghost" className="text-danger-fg" onClick={() => setCancelling(true)}>
                Cancel delivery
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                {d.status === 'draft' && (
                  <Button onClick={() => act.mutate('confirm')} disabled={act.isPending || d.missing_prices > 0}>
                    Confirm
                  </Button>
                )}
                {d.status === 'confirmed' && (
                  <Button onClick={() => act.mutate('deliver')} disabled={act.isPending}>
                    Mark delivered
                  </Button>
                )}
              </div>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      <DeliveryForm open={editing && !!d} delivery={d} onClose={() => setEditing(false)} />

      <Dialog open={cancelling} onOpenChange={setCancelling}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel this delivery?</DialogTitle>
            <DialogDescription>It won’t be billed. It stays in the list under Cancelled, with its history.</DialogDescription>
          </DialogHeader>
          <Field label="Reason" hint="Optional">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Entered twice" />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(false)}>
              Keep it
            </Button>
            <Button variant="destructive" onClick={() => act.mutate('cancel')} disabled={act.isPending}>
              Cancel delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
